import React, { useState } from "react";
import classNames from "classnames";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import {
  useRetrieveCurrentPlanzone,
  useZoneRetrieval,
} from "@planzone/view-models";
import { usePreferences } from "@components/preferences";
import { useTranslation } from "react-i18next";

import { Props } from "./types";
import { ProjectHistory } from "./projectHistory";
import { Navigation } from "./navigation";
import { Logo } from "./logo";
import { CloseButton } from "./closeButton";
import { OpenButton } from "./openButton";
import { PlanzoneKnowledge } from "./planzoneKnowledge";
import { Version } from "./version";
import { PanelResizer } from "./panelResizer";
import styles from "./styles.scss";

function Layout(props: Props) {
  const { i18n } = useTranslation();
  const id = React.useId();
  const { state, edit } = usePreferences();
  const { zone } = useZoneRetrieval();
  const { planzone } = useRetrieveCurrentPlanzone();
  const push = useNavigate();
  const { pathname } = useLocation();
  const [isSubMenuShown, setIsSubMenuShown] = useState(
    pathname === "/members" || pathname === "/cost-profiles",
  );
  const [isUtilitiesShown, setIsUtilitiesShown] = useState(
    pathname === "/utilities",
  );
  const [isZonesShown, setIsZonesShown] = useState(true);
  const [isStrategyShown, setIsStrategyShown] = useState(
    pathname === "/strategic-axes",
  );
  const sidebar = (
    <div key="sidebar" className={styles.sideBar}>
      <div className={styles.sideBarHeader}>
        {!state.isLayoutSidebarMinimized ? (
          <div className={styles.logoBox} onClick={() => push("/")}>
            <div className={styles.flexWrapper}>
              <Logo className={styles.logo} />
              <h1 className={styles.planzone}>{planzone.name}</h1>
            </div>
          </div>
        ) : (
          <div className={styles.logoBox} onClick={() => push("/")}>
            <Logo className={styles.logo} />
          </div>
        )}
        <div
          onClick={() =>
            edit({
              ...state,
              isLayoutSidebarMinimized: !state.isLayoutSidebarMinimized,
            })
          }
        >
          {!state.isLayoutSidebarMinimized ? (
            <CloseButton className={styles.button} />
          ) : (
            <OpenButton className={styles.button} />
          )}
        </div>
      </div>
      {!state.isLayoutSidebarMinimized && (
        <div className={styles.projectHistory}>
          <ProjectHistory
            selectProject={({ uuid }) => push(`/projects/${uuid}`)}
          />
        </div>
      )}
      <div className={styles.navigation}>
        <Navigation
          isOpen={!state.isLayoutSidebarMinimized}
          isSubMenuShown={isSubMenuShown}
          setIsSubMenuShown={setIsSubMenuShown}
          isUtilitiesShown={isUtilitiesShown}
          setIsUtilitiesShown={setIsUtilitiesShown}
          isStrategyShown={isStrategyShown}
          setIsStrategyShown={setIsStrategyShown}
          isZonesShown={isZonesShown}
          setIsZonesShown={setIsZonesShown}
          planzone={planzone}
          zone={zone}
        />
      </div>
      <div className={styles.footer}>
        <Version />
        {/fr/i.test(i18n.language) && <PlanzoneKnowledge />}
      </div>
    </div>
  );

  return (
    <div
      className={classNames(styles.layout, {
        [styles.closed]: state.isLayoutSidebarMinimized,
      })}
    >
      <PanelResizer
        leftPanel={sidebar}
        rightPanel={
          <div key={id} className={styles.main}>
            {props.children}
          </div>
        }
        isMinimized={state.isLayoutSidebarMinimized}
      />
    </div>
  );
}

export { Layout };
